import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import {canAccessSubroot, getLoginSubroot, getSubRoot, getUrl, setLoginSubroot} from '../utils.jsx';

// ==============================|| AUTH GUARD ||============================== //

export default function AuthGuard({ children }) {
  const { isLoggedIn, logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // A page under a subroot the user can't see (user.visibleOrganizations, computed by the backend):
  // forced logout, then the effect below redirects to the login of the current subroot. The check
  // doesn't depend on the loginSubroot of localStorage (missing, edited or never set, e.g. after a
  // plugin login): it runs on every logged in page. A visible subroot becomes the login subroot
  // (e.g. for the logout redirect)
  const subroot = getSubRoot(location.pathname);
  const canSeeSubroot = canAccessSubroot(user, location.pathname);
  const otherSubroot = isLoggedIn && !!subroot && !canSeeSubroot;

  useEffect(() => {
    if (isLoggedIn && canSeeSubroot && subroot && getLoginSubroot() !== subroot) setLoginSubroot(subroot);
  }, [isLoggedIn, canSeeSubroot, subroot]);

  const loggingOut = useRef(false);
  useEffect(() => {
    if (otherSubroot && !loggingOut.current) {
      loggingOut.current = true;
      logout().finally(() => {
        loggingOut.current = false;
      });
    }
  }, [otherSubroot, logout]);

  useEffect(() => {
    if (!isLoggedIn) {
      const target = location?.state?.from || 'login';
      const redirectUrl = getUrl(location.pathname, target);

      navigate(redirectUrl, {
        state: {
          from: location.pathname
        },
        replace: true
      });
    }
  }, [isLoggedIn, navigate, location]);

  // don't render the page of the other subroot while logging out
  if (otherSubroot) return null;

  return children;
}

AuthGuard.propTypes = { children: PropTypes.any };
